/**
 * Rhythmz — Scene: Basilisk Skull
 * ──────────────────────────────────
 * A massive demonic skull with jaw mechanics, glowing eye sockets, and
 * crack propagation. Bone-colored with hot magenta eye glow and amber mouth.
 *
 * Audio: Bass → jaw open distance + eye glow. Beat → jaw snap. Treble → cracks.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, vec3, vec4, float, sin, fract, dot, floor, abs,
  add, sub, mul, mix, pow, normalize,
  positionLocal, normalLocal,
} from 'three/tsl';
import { SceneLighting, type AudioRef } from './SceneLighting';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface SkullUniforms {
  uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N;
  uCol0: N; uCol1: N; uCol2: N;
}

const hash3 = Fn(([p]: [N]) => fract(sin(dot(p, vec3(127.1, 311.7, 74.7))).mul(float(43758.5453))));
const vnoise3 = Fn(([p]: [N]) => {
  const i = floor(p); const f: N = fract(p);
  const ux = mul(f.x, f.x, sub(float(3.0), mul(float(2.0), f.x)));
  const uy = mul(f.y, f.y, sub(float(3.0), mul(float(2.0), f.y)));
  const uz = mul(f.z, f.z, sub(float(3.0), mul(float(2.0), f.z)));
  const n000 = hash3(i), n100 = hash3(add(i, vec3(1,0,0)));
  const n010 = hash3(add(i, vec3(0,1,0))), n110 = hash3(add(i, vec3(1,1,0)));
  const n001 = hash3(add(i, vec3(0,0,1))), n101 = hash3(add(i, vec3(1,0,1)));
  const n011 = hash3(add(i, vec3(0,1,1))), n111 = hash3(add(i, vec3(1,1,1)));
  return mix(mix(mix(n000,n100,ux), mix(n010,n110,ux), uy), mix(mix(n001,n101,ux), mix(n011,n111,ux), uy), uz);
});
const fbm3 = Fn(([p]: [N]) => {
  let v: N = float(0.0); let a: N = float(0.5); let freq: N = float(1.0);
  v = add(v, mul(a, vnoise3(mul(p, freq)))); freq = mul(freq, float(2.0)); a = mul(a, float(0.5));
  v = add(v, mul(a, vnoise3(mul(p, freq)))); freq = mul(freq, float(2.0)); a = mul(a, float(0.5));
  v = add(v, mul(a, vnoise3(mul(p, freq)))); freq = mul(freq, float(2.0)); a = mul(a, float(0.5));
  v = add(v, mul(a, vnoise3(mul(p, freq))));
  return v;
});

export function createBasiliskSkull(u: SkullUniforms): SceneHandle {
  const group = new THREE.Group();
  const lighting = new SceneLighting({
    keyColor: new THREE.Color(1.0, 0.7, 0.3),    // warm amber from below
    keyIntensity: 8,
    keyPosition: new THREE.Vector3(2, -4, 5),
    fillColor: new THREE.Color(0.2, 0.1, 0.15),  // dim red fill
    fillIntensity: 3,
    fillPosition: new THREE.Vector3(-3, 2, 3),
    rimColor: new THREE.Color(0.5, 0.1, 0.4),    // dark magenta rim
    rimIntensity: 5,
    rimPosition: new THREE.Vector3(0, 2, -6),
    camLightIntensity: 1.5,
  });
  group.add(lighting.group);

  // ── Cranium: sphere with bone-like displacement ──
  const craniumGeo = new THREE.SphereGeometry(0.8, 5, 5);
  const boneMat = new THREE.MeshStandardNodeMaterial({
    metalness: 0.1,
    roughness: 0.6,
    color: 0x8a8070,
  });

  boneMat.positionNode = Fn(() => {
    const pos = positionLocal;
    const norm = normalLocal;
    const n = fbm3(add(mul(pos, float(2.0)), mul(u.uTime, float(0.15))));
    // Bone texture: subtle bumps
    const boneDisp = mul(sub(n, float(0.5)), float(0.08));
    // Eye sockets: push inward at specific positions
    const eyeL = sub(float(1.0), abs(dot(normalize(pos), vec3(-0.4, 0.2, 0.7))));
    const eyeR = sub(float(1.0), abs(dot(normalize(pos), vec3(0.4, 0.2, 0.7))));
    const socketL = mul(pow(eyeL, float(8.0)), float(-0.2));
    const socketR = mul(pow(eyeR, float(8.0)), float(-0.2));
    // Treble-driven crack propagation
    const crack = mul(pow(abs(sub(n, float(0.5))), float(4.0)), mul(u.uTreble, float(0.3)));
    // Bass jitter
    const jitter = mul(u.uBass, float(0.02));
    return add(add(add(add(pos, mul(norm, boneDisp)), mul(norm, add(socketL, socketR))), mul(norm, crack)), vec3(jitter, jitter, jitter));
  })();

  boneMat.normalNode = Fn(() => {
    const pos = positionLocal;
    const n = fbm3(add(mul(pos, float(2.0)), mul(u.uTime, float(0.15))));
    const eps = float(0.04);
    const nX = fbm3(add(mul(pos, float(2.0)), add(vec3(eps, 0, 0), mul(u.uTime, float(0.15)))));
    const nY = fbm3(add(mul(pos, float(2.0)), add(vec3(0, eps, 0), mul(u.uTime, float(0.15)))));
    return normalize(vec3(sub(nX, n), sub(nY, n), float(0.5)));
  })();

  const cranium = new THREE.Mesh(craniumGeo, boneMat);
  cranium.position.set(0, 0.3, 0);
  group.add(cranium);

  // ── Eye glow: two emissive spheres inside sockets ──
  const eyeGeo = new THREE.SphereGeometry(0.1, 8, 8);
  const eyeGlowMat = new THREE.MeshStandardNodeMaterial({ metalness: 0.0, roughness: 0.0 });
  eyeGlowMat.emissiveNode = Fn(() => {
    const intensity = add(float(0.5), mul(u.uBass, float(2.0)), mul(u.uBeat, float(1.5)));
    return vec4(mul(float(1.0), intensity), mul(float(0.1), intensity), mul(float(0.5), intensity), float(1.0));
  })();
  const eyeL = new THREE.Mesh(eyeGeo, eyeGlowMat);
  eyeL.position.set(-0.3, 0.5, 0.55);
  group.add(eyeL);
  const eyeR = new THREE.Mesh(eyeGeo, eyeGlowMat);
  eyeR.position.set(0.3, 0.5, 0.55);
  group.add(eyeR);

  // ── Jaw: lower section, hinge-rotated ──
  const jawGeo = new THREE.SphereGeometry(0.6, 4, 3, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.4);
  const jaw = new THREE.Mesh(jawGeo, boneMat);
  jaw.position.set(0, -0.3, 0.1);
  group.add(jaw);

  // ── Teeth: instanced cones along jaw ──
  const teethCount = 8;
  const teethGeo = new THREE.ConeGeometry(0.04, 0.15, 4);
  const teeth = new THREE.InstancedMesh(teethGeo, boneMat, teethCount);
  teeth.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(teeth);

  const teethData: { x: number; z: number }[] = [];
  for (let i = 0; i < teethCount; i++) {
    const t = i / (teethCount - 1);
    teethData.push({ x: -0.35 + t * 0.7, z: 0.3 + Math.abs(t - 0.5) * 0.2 });
  }

  // ── Animation ──
  let t = 0;
  const pivot = new THREE.Object3D();

  const update = (dt: number): void => {
    t += dt;
    const audio: AudioRef = {
      bass: u.uBass.value ?? 0, beat: u.uBeat.value ?? 0, mid: u.uMid.value ?? 0,
      level: ((u.uBass.value ?? 0) + (u.uMid.value ?? 0)) * 0.5,
    };
    lighting.update(audio);

    // Slow menacing rotation
    group.rotation.y = Math.sin(t * 0.12) * 0.3;
    group.rotation.x = Math.sin(t * 0.08) * 0.05;

    // Jaw mechanics: opens with bass, snaps on beat
    const jawOpen = (u.uBass.value ?? 0) * 0.5 + Math.max(0, (u.uBeat.value ?? 0) - 0.5) * 0.4;
    jaw.rotation.x = -jawOpen; // hinge around top of jaw
    jaw.position.y = -0.3 - jawOpen * 0.15;

    // Teeth follow jaw
    for (let i = 0; i < teethCount; i++) {
      const td = teethData[i];
      pivot.position.set(td.x, -0.35 - jawOpen * 0.2, td.z);
      pivot.rotation.x = Math.PI; // point down
      pivot.scale.set(1, 1, 1);
      pivot.updateMatrix();
      teeth.setMatrixAt(i, pivot.matrix);
    }
    teeth.instanceMatrix.needsUpdate = true;

    // Cranium jitter on bass
    cranium.position.x = Math.sin(t * 20) * (u.uBass.value ?? 0) * 0.02;
  };

  const dispose = (): void => {
    lighting.dispose();
    craniumGeo.dispose(); boneMat.dispose();
    eyeGeo.dispose(); eyeGlowMat.dispose();
    jawGeo.dispose();
    teethGeo.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Basilisk Skull' };
}